import { useEffect, useState } from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import { invoke, isTauri } from '../tauri.js';
import { useStore } from '../store.js';

interface SecretStatus {
  integration: string;
  status: 'resolved' | 'missing';
  source: 'env' | 'infisical' | 'none';
}

/**
 * Surface the status of each `integrations:` entry. Desktop queries the
 * Rust host (env-var fallback today; Infisical SDK is a tracked
 * follow-up — see complaints/2026-04-22-infisical-not-wired.complaint).
 * The web build hides the panel because there's nowhere to resolve
 * secrets from in-browser.
 */
export function IntegrationsPanel() {
  const integrations = useStore((s) => s.doc?.integrations);
  const [statuses, setStatuses] = useState<SecretStatus[] | null>(null);

  useEffect(() => {
    if (!isTauri() || !integrations || integrations.length === 0) {
      setStatuses(null);
      return;
    }
    invoke<SecretStatus[]>('check_integrations', { integrations })
      .then(setStatuses)
      .catch((e) => console.error('check_integrations failed', e));
  }, [integrations]);

  if (!isTauri() || !statuses || statuses.length === 0) return null;

  return (
    <div className="border-b border-ink-800 bg-ink-900/40 px-3 py-2 text-[11px]">
      <div className="mb-1 flex items-center gap-2">
        <span className="label">Integrations</span>
        <span className="mono text-ink-500">(read-only)</span>
      </div>
      <ul className="flex flex-wrap gap-2">
        {statuses.map((s) => (
          <li
            key={s.integration}
            className="mono flex items-center gap-1 rounded border border-ink-800 bg-ink-950 px-2 py-1"
          >
            {s.status === 'resolved' ? (
              <CheckCircle2 size={11} className="text-status-deployed" />
            ) : (
              <XCircle size={11} className="text-status-deprecated" />
            )}
            <span className="text-ink-200">{s.integration}</span>
            <span className="text-ink-500">· {s.source}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
